import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, pick } from "@/contexts/LanguageContext";
import { openWhatsApp } from "@/lib/whatsapp";
import SeoHead from "@/components/SeoHead";
import { pathFor, ROUTE_KEYS } from "@/i18n/routeMap";
import { buildLocalBusinessJsonLd, buildBreadcrumb, buildFaqJsonLd } from "@/lib/seo";
import { FAQ_CATEGORIES, FAQ_ITEMS, BRAND_DISCLAIMER_FAQ } from "@/lib/faqData";

const COPY = {
  "en": {
    "title": "Rental FAQ",
    "subtitle": "Frequently asked questions about renting a luxury car in Benidorm, Alicante, Costa Blanca, Valencia and Murcia."
  },
  "es": {
    "title": "Preguntas frecuentes de alquiler",
    "subtitle": "Preguntas frecuentes sobre alquilar un coche de lujo en Benidorm, Alicante, Costa Blanca, Valencia y Murcia."
  },
  "ru": {
    "title": "Часто задаваемые вопросы по аренде",
    "subtitle": "Ответы на популярные вопросы об аренде премиального автомобиля в Бенидорме, Аликанте, на Коста-Бланке, в Валенсии и Мурсии."
  },
  "fr": {
    "title": "FAQ location de voiture de luxe",
    "subtitle": "Questions fréquentes sur la location d'un véhicule de luxe à Benidorm, Alicante, Costa Blanca, Valence et Murcie."
  },
  "de": {
    "title": "Häufige Fragen zur Luxusmietwagen",
    "subtitle": "Häufig gestellte Fragen zur Anmietung eines Luxusfahrzeugs in Benidorm, Alicante, Costa Blanca, Valencia und Murcia."
  },
  "it": {
    "title": "Domande frequenti sul noleggio",
    "subtitle": "Domande frequenti sul noleggio di un'auto di lusso a Benidorm, Alicante, Costa Blanca, Valencia e Murcia."
  },
  "nl": {
    "title": "Veelgestelde vragen verhuur",
    "subtitle": "Veelgestelde vragen over het huren van een luxe auto in Benidorm, Alicante, Costa Blanca, Valencia en Murcia."
  },
  "pl": {
    "title": "FAQ – wynajem luksusowych samochodów",
    "subtitle": "Najczęściej zadawane pytania dotyczące wynajmu luksusowego samochodu w Benidorm, Alicante, Costa Blanca, Walencji i Murcji."
  }
};

export default function RentalFAQ() {
  const { lang, t } = useLang();
  const [settings, setSettings] = useState({});
  const c = COPY[lang] || COPY.en;

  useEffect(() => { (async () => { try { const { data } = await api.get("/settings"); setSettings(data || {}); } catch { /* */ } })(); }, []);

  const allFaqs = FAQ_CATEGORIES.flatMap((cat) => FAQ_ITEMS[cat.key]);
  const jsonLd = [
    buildLocalBusinessJsonLd(settings),
    buildBreadcrumb([{ name: c.title, path: pathFor(ROUTE_KEYS.RENTAL_FAQ, lang) }]),
    buildFaqJsonLd(allFaqs, lang),
  ];

  return (
    <div className="bg-[#0C0C0C] min-h-screen" data-testid="rental-faq-page">
      <SeoHead title={c.title} description={c.subtitle} lang={lang} pageKey={ROUTE_KEYS.RENTAL_FAQ} jsonLd={jsonLd} />

      <div className="pt-32 md:pt-40 pb-24 px-6 md:px-10 max-w-4xl mx-auto">
        <nav className="text-[11px] tracking-[0.18em] uppercase text-[#8A8A8A] flex items-center gap-2 mb-4">
          <Link to={pathFor(ROUTE_KEYS.HOME, lang)} className="hover:text-[#C9A55A]">{t.nav.home || "Home"}</Link>
          <ChevronRight size={12} />
          <span className="text-[#C9A55A]">{c.title}</span>
        </nav>
        <h1 className="font-display text-[#F5F1EB] text-4xl md:text-6xl">{c.title}</h1>
        <p className="text-[#F5F1EB]/70 font-light mt-4 max-w-2xl">{c.subtitle}</p>

        <div className="mt-12 space-y-12">
          {FAQ_CATEGORIES.map((cat) => (
            <section key={cat.key} data-testid={`faq-cat-${cat.key}`}>
              <h2 className="font-display text-[#C9A55A] text-2xl mb-4">{pick(cat.title, lang)}</h2>
              <div className="space-y-3">
                {FAQ_ITEMS[cat.key].map((f, i) => (
                  <details key={i} className="border border-[#2A2A2A] p-4 group" data-testid={`faq-${cat.key}-${i}`}>
                    <summary className="cursor-pointer text-[#F5F1EB] text-sm font-semibold list-none flex items-center justify-between gap-3">
                      <span>{pick(f.q, lang)}</span>
                      <ChevronRight size={14} className="text-[#C9A55A] transition-transform group-open:rotate-90"/>
                    </summary>
                    <p className="mt-3 text-[#F5F1EB]/80 text-sm font-light leading-relaxed">{pick(f.a, lang)}</p>
                  </details>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 border border-[#2A2A2A] p-6" data-testid="faq-brand-disclaimer">
          <div className="text-overline text-[#C9A55A] mb-2">Ferrari · Lamborghini · BMW M · G-Class · Wagoneer · Rolls-Royce</div>
          <p className="text-[#F5F1EB]/70 text-sm font-light leading-relaxed">{BRAND_DISCLAIMER_FAQ[lang] || BRAND_DISCLAIMER_FAQ.en}</p>
        </div>

        <div className="mt-10 text-center">
          <button onClick={() => openWhatsApp(t.whatsapp.generic, settings, lang)} className="btn-red" data-testid="faq-wa-btn">
            <MessageCircle size={16} /> {t.contact.cta}
          </button>
        </div>
      </div>
    </div>
  );
}
